import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// MUI
import { Grid, CardActions, Collapse, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

// Icons
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SimpleIconButton from "@Layouts/components/Buttons/SimpleIconButton";

/**
 * Properties.
 * addClassName: String - Add a class to the container grid of the component.
 * addStyle: String - Add custom style to the container grid of the component.
 * title: String - Prints the title of the collapsible component.
 * contentClass: String - The class for the content container. This is the <Collapse> tag.
 * isExpanded: Boolean - Collapse and expand the component programatically.
 * content: String - Content to be added inside the hidden section when expanded is false.
 * children - Is what is placed between the opening and closing tags.
 */

const CardActionsStyled = styled(CardActions)({
	display: "flex",
	flexDirection: "row",
});

const CollapseStyled = styled(Collapse)({
	border: "none",
});

const GridStyled = styled(Grid)({
	width: "100%",
	border: "none",
	padding: "0px",
	margin: "0px",
});

const GridTitleStyled = styled(Grid)({
	fontFamily: "Montserrat",
	fontSize: "22px",
	fontWeight: "500",
	top: "20px",
	fontVariant: "small-caps",
	cursor: "pointer",
	userSelect: "none",
});

const classes = {
	titleGrey: {
		color: "var(--text-color)",
	},
	titleBlue: {
		color: "var(--light-blue-color)",
	},
};

export const ExpandMore = styled((props) => {
	const { ...other } = props;
	return <IconButton {...other} />;
})(({ theme, expand }) => ({
	transform: expand !== "expanded" ? "rotate(0deg)" : "rotate(180deg)",
	marginLeft: "auto",
	transition: theme.transitions.create("transform", {
		duration: theme.transitions.duration.shortest,
	}),
}));

export default function CollapsibleCard(props) {
	const [expanded, setExpanded] = useState(props.expanded ?? false);

	const handleExpandClick = () => setExpanded(!expanded);
	const toogleColor = (expandedStatus) => (expandedStatus ? classes.titleBlue : classes.titleGrey);

	useEffect(() => {
		setExpanded(Boolean(props.expanded));

		return () => {
			setExpanded(false);
		};
	}, [props]);

	return (
		<GridStyled className={props.addClassName} style={props.addStyle}>
			<CardActionsStyled
				sx={{ marginBottom: props.marginBottom, cursor: "pointer", ...props.sxActions }}
				onClick={handleExpandClick}
				style={{ position: "relative" }}
				className={props.innerClass}
			>
				{props?.icon && <SimpleIconButton icon={props.icon} />}
				{props?.title && <GridTitleStyled className={toogleColor(expanded)}>{props.title}</GridTitleStyled>}
				{props?.customTitle && (
					<Typography className={`${props.customTitleClasses} ${expanded ? "!text-[#2357BE]" : "!text-[#666666]"}`}>
						{props?.customTitle}
					</Typography>
				)}
				{!props?.hideCollapseIcon && (
					<ExpandMore expand={expanded ? "expanded" : "normal"}>
						<ExpandMoreIcon />
					</ExpandMore>
				)}
			</CardActionsStyled>
			<CollapseStyled in={expanded} timeout="auto" unmountOnExit className={props.contentClass}>
				{props.content} {props.children}
			</CollapseStyled>
		</GridStyled>
	);
}

CollapsibleCard.propTypes = {
	addClassName: PropTypes.string,
	addStyle: PropTypes.object,
	title: PropTypes.string,
	contentClass: PropTypes.string,
	expanded: PropTypes.bool,
	content: PropTypes.string,
	children: PropTypes.node,
	marginBottom: PropTypes.number,
	hideCollapseIcon: PropTypes.bool,
	sxActions: PropTypes.object,
	innerClass: PropTypes.string,
	customTitle: PropTypes.string,
	customTitleClasses: PropTypes.string,
	icon: PropTypes.node,
};
